import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, FlaskConical, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';

const STATUS_COLOR = {
  ok:     'text-buy border-buy',
  failed: 'text-sell border-sell',
};

/**
 * TestRunnerSection — Phase 51.E Lab section.
 * Operator picks a preview build → backend produces a coverage report
 * with concrete pytest invocations. Read-only in preview environment.
 */
export default function TestRunnerSection({ isProd }) {
  const [runs, setRuns]         = useState([]);
  const [previews, setPreviews] = useState([]);
  const [previewId, setPreviewId] = useState('');
  const [busy, setBusy]         = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = () =>
    api.get('/lab/tests/list?limit=50')
      .then(({ data }) => setRuns(data.test_runs || []))
      .catch(() => {});

  useEffect(() => {
    load();
    api.get('/lab/preview/list?limit=50')
      .then(({ data }) => setPreviews(data.previews || []))
      .catch(() => {});
  }, []);

  const run = async () => {
    if (!previewId || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/lab/tests/run/${previewId}`);
      toast.success(`Tests reported · ${data.coverage_score}% covered`);
      setSelectedId(data.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test run failed');
    } finally {
      setBusy(false);
    }
  };

  const selected = useMemo(
    () => runs.find((r) => r.id === selectedId),
    [runs, selectedId],
  );

  return (
    <div data-testid="lab-tests" className="space-y-3">
      {/* INPUT */}
      <div className="border border-cyan-sys p-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">
          // run test coverage report
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <select
            data-testid="lab-tests-preview-select"
            value={previewId}
            onChange={(e) => setPreviewId(e.target.value)}
            disabled={!isProd || busy}
            className="flex-1 bg-transparent border border-[var(--border-default)] px-2 py-2 text-[11px] font-mono focus:border-cyan-sys outline-none"
          >
            <option value="">— select preview build —</option>
            {previews.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id.slice(0, 8)} · {(p.files || []).length} file(s) · {p.status}
              </option>
            ))}
          </select>
          <button
            data-testid="lab-tests-run"
            onClick={run}
            disabled={!isProd || busy || !previewId}
            className="flex items-center justify-center gap-1.5 border border-cyan-sys text-cyan-sys px-3 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <FlaskConical size={11} />}
            {busy ? 'analyzing...' : 'run tests'}
          </button>
        </div>
        <div className="text-[10px] text-[var(--txt-secondary)] mt-2">
          {isProd
            ? 'Computes pytest coverage and AST-compiles touched backend modules. No tests are executed; the report lists ready-to-run pytest commands.'
            : '⚠ Test Runner is production-only. List below is read-only here.'}
        </div>
      </div>

      {/* LIST + DETAIL */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--txt-secondary)] mb-1">
            {runs.length} test run(s)
          </div>
          {runs.length === 0 ? (
            <div className="border border-[var(--border-default)] p-4 text-[11px] text-[var(--txt-secondary)]">
              No test runs yet. Pick a preview build above and click “run tests”.
            </div>
          ) : (
            runs.map((r) => (
              <button
                key={r.id}
                data-testid={`lab-tests-${r.id}`}
                onClick={() => setSelectedId(r.id)}
                className={`w-full text-left border p-2.5 transition-colors ${
                  r.id === selectedId
                    ? 'border-cyan-sys bg-[var(--bg-panel-lighter)]'
                    : 'border-[var(--border-default)] hover:border-cyan-sys'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[11px] text-[var(--txt-primary)] truncate font-mono">
                    {r.id.slice(0, 8)} · {r.coverage_score}% covered
                  </span>
                  <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_COLOR[r.status] || 'text-[var(--txt-muted)] border-[var(--border-default)]'}`}>
                    {r.status}
                  </span>
                </div>
                <div className="text-[9px] text-[var(--txt-secondary)] truncate">
                  {(r.created_at || '').slice(0, 19).replace('T', ' ')}Z ·
                  {' '}preview {String(r.preview_id || '').slice(0, 8)} ·
                  {' '}{(r.touched || []).length} touched
                </div>
              </button>
            ))
          )}
        </div>

        {/* DETAIL */}
        <div className="border border-[var(--border-default)] p-3 min-h-[420px]">
          {!selected ? (
            <div className="text-[11px] text-[var(--txt-secondary)]">Select a test run to inspect.</div>
          ) : (
            <div className="space-y-3 text-[11px]">
              <div className="flex items-center justify-between">
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys">// test report</div>
                <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${STATUS_COLOR[selected.status] || ''}`}>
                  {selected.status} · {selected.coverage_score}%
                </span>
              </div>
              <div className="text-[10px] font-mono text-[var(--txt-secondary)]">
                test_run: {selected.id} · preview: {selected.preview_id} ·
                by: {selected.created_by}
              </div>

              {/* Coverage map */}
              <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-1">// coverage</div>
                {(selected.coverage || []).length === 0 ? (
                  <div className="text-[10px] text-[var(--txt-secondary)]">
                    No backend modules touched.
                  </div>
                ) : (
                  <ul className="space-y-0.5">
                    {selected.coverage.map((c, i) => (
                      <li key={i} className="text-[10px] font-mono flex items-start gap-1.5">
                        {c.covering_tests?.length > 0
                          ? <CheckCircle2 size={11} className="text-buy mt-0.5 shrink-0" />
                          : <XCircle size={11} className="text-sell mt-0.5 shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-[var(--txt-primary)] truncate">{c.source}</div>
                          <div className="text-[var(--txt-secondary)] text-[9px]">
                            {c.covering_tests?.length
                              ? `tests: ${c.covering_tests.join(', ')}`
                              : 'no covering tests'}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Compile status */}
              {(selected.compile_status || []).length > 0 && (
                <div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-1">// compile</div>
                  <ul className="space-y-0.5">
                    {selected.compile_status.map((c, i) => (
                      <li key={i} className="text-[10px] font-mono flex items-start gap-1.5">
                        {c.compiles === true && <CheckCircle2 size={11} className="text-buy mt-0.5 shrink-0" />}
                        {c.compiles === false && <XCircle size={11} className="text-sell mt-0.5 shrink-0" />}
                        {c.compiles === null && <MinusCircle size={11} className="text-hold mt-0.5 shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-[var(--txt-primary)] truncate">{c.file}</div>
                          {c.error && (
                            <div className="text-sell text-[9px]">{c.error} (line {c.line})</div>
                          )}
                          {c.note && (
                            <div className="text-[var(--txt-secondary)] text-[9px]">{c.note}</div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Pytest commands */}
              <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-1">// pytest commands</div>
                <pre className="p-2 text-[10px] font-mono text-[var(--txt-primary)] whitespace-pre overflow-x-auto border border-[var(--border-default)] bg-[var(--bg-panel-lighter)]">
                  {(selected.pytest_commands || []).join('\n')}
                </pre>
              </div>

              {(selected.playwright_commands || []).length > 0 && (
                <div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-1">// playwright targets</div>
                  <pre className="p-2 text-[10px] font-mono text-[var(--txt-primary)] whitespace-pre overflow-x-auto border border-[var(--border-default)] bg-[var(--bg-panel-lighter)]">
                    {selected.playwright_commands.join('\n')}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
